/**
 * API functions for the new date-range report endpoints.
 *
 * These call the /reportrange/ backend URLs and are used by
 * PerformanceOverviewReport and TeamExpensesReport when a startDate is
 * supplied (i.e. the user has selected a multi-month range in ReportsPage).
 *
 * The existing single-date functions in metric.js / expenseBreakdown.js /
 * growthPlan.js are left untouched so legacy reports continue to work.
 */
import { get } from "./api";

const base = (customerId, path) =>
  `/api/v2/customers/${customerId}/reportrange/${path}`;

/** Aggregated income / expense / profit totals + change % for a date range. */
export const getRangeMetricData = (customerId, startDate, endDate, signal, params = {}) =>
  get(base(customerId, `metricview/${startDate}/${endDate}/`), params, false, signal, true);

/** Monthly income / expense / profit breakdown for a date range. */
export const getRangeMetricGraphData = (customerId, startDate, endDate, signal, params = {}) =>
  get(base(customerId, `metricgraph/${startDate}/${endDate}/`), params, false, signal, true);

/** Per-expense-type total for a date range (team, marketing, overhead, cogs …). */
export const getRangeAdvancedMetricSummary = (customerId, metricType, startDate, endDate, signal, params = {}) =>
  get(base(customerId, `advancedmetrics/${metricType}/${startDate}/${endDate}/`), params, false, signal, true);

/** Monthly trend for a specific metric_type over a date range. */
export const getRangeAdvancedMetricTrend = (customerId, metricType, startDate, endDate, signal, params = {}) =>
  get(base(customerId, `advancedtrend/${metricType}/${startDate}/${endDate}/`), params, false, signal, true);

/** Category breakdown totals for a date range. */
export const getRangeCategoryBreakdown = (customerId, startDate, endDate, group, signal, params = {}) =>
  get(base(customerId, `categorybreakdown/${startDate}/${endDate}/${group}/`), params, false, signal, true);

/** P&L target values aggregated over a date range. */
export const getRangePLValues = (customerId, startDate, endDate, signal, params = {}) =>
  get(base(customerId, `plvalues/${startDate}/${endDate}/`), params, false, signal, true);

/** Growth-plan / cash-on-hand data (balance-sheet is point-in-time, uses endDate). */
export const getRangeGrowthPlanData = (customerId, startDate, endDate, signal, params = {}) =>
  get(base(customerId, `growthplan/${startDate}/${endDate}/`), params, false, signal, true);
